import { Component, OnInit, inject, signal } from '@angular/core';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InstallmentPlanRecord, SalesApiService } from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-installment-plans',
  imports: [AinAmountPipe, AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, InputTextModule],
  template: `
    <ain-page [title]="t('sales.installments.title')" [description]="t('sales.installments.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15" [selection]="selectedRows()" (selectionChange)="onSelectionChange($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.installments.fields.planNumber') }}</th>
                <th>{{ t('sales.installments.fields.principalAmount') }}</th>
                <th>{{ t('sales.installments.fields.totalPayable') }}</th>
                <th>{{ t('sales.installments.fields.installmentCount') }}</th>
                <th>{{ t('columns.status') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.planNumber }}</button></td>
                <td>{{ (row.principalAmount ?? row.totalAmount) | ainAmount }} {{ row.currencyCode }}</td>
                <td>{{ row.totalAmount | ainAmount }} {{ row.currencyCode }}</td>
                <td>{{ row.installmentCount }}</td>
                <td>{{ row.status }}</td>
              </tr>
            </ng-template>
          </p-table>
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`.panel{display:grid;gap:10px}.toolbar{display:flex;gap:8px;flex-wrap:wrap}.link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}.error{color:#b91c1c}`]
})
export class SalesInstallmentPlansComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly error = signal('');
  readonly rows = signal<InstallmentPlanRecord[]>([]);
  readonly selectedRows = signal<InstallmentPlanRecord[]>([]);
  ngOnInit(): void { this.load(); }
  load(): void {
    this.loading.set(true);
    this.api.listInstallmentPlans().subscribe({
      next: items => { this.rows.set(items); this.loading.set(false); },
      error: () => { this.rows.set([]); this.error.set(this.t('sales.installments.errorLoad')); this.loading.set(false); }
    });
  }
  onSelectionChange(s: InstallmentPlanRecord[] | InstallmentPlanRecord | null): void {
    this.selectedRows.set(Array.isArray(s) ? s : s ? [s] : []);
  }
  openCreate(): void { void this.router.navigate(['/sales/installment-plans/new']); }
  openSelected(): void { const r = this.selectedRows()[0]; if (r) this.openDetail(r.id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/installment-plans', id]); }
  t(key: string): string { return this.localization.translate(key); }
}
